import { useCallback, useEffect, useRef, useState } from "react";

import { flushSync } from "react-dom";

const HIGHLIGHT_MS = 1800;
/** Slow, steady glide so users can track which row moved. */
const FLIP_MS = 820;
const FLIP_EASING = "cubic-bezier(0.33, 0.1, 0.25, 1)";

/** Brief highlight on the item that just moved (list move or card drop). */
export const useReorderHighlight = (durationMs = HIGHLIGHT_MS) => {
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const flash = useCallback(
    (id: string | null) => {
      if (!id) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      setHighlightId(id);
      timerRef.current = setTimeout(() => {
        setHighlightId(null);
        timerRef.current = null;
      }, durationMs);
    },
    [durationMs]
  );

  const isHighlighted = useCallback(
    (id: string) => highlightId === id,
    [highlightId]
  );

  return { highlightId, flash, isHighlighted };
};

/**
 * FLIP-animate children with `[data-reorder-id]` after a sync DOM reorder.
 * `apply` should update React state (wrapped in flushSync by the caller helper).
 */
export const animateReorderFlip = (
  container: HTMLElement | null | undefined,
  apply: () => void
) => {
  if (!container) {
    apply();
    return;
  }

  const nodes = Array.from(
    container.querySelectorAll<HTMLElement>("[data-reorder-id]")
  );
  const first = new Map<string, DOMRect>();
  nodes.forEach(node => {
    const id = node.dataset.reorderId;
    if (id) first.set(id, node.getBoundingClientRect());
  });

  apply();

  requestAnimationFrame(() => {
    const nextNodes = Array.from(
      container.querySelectorAll<HTMLElement>("[data-reorder-id]")
    );
    nextNodes.forEach(node => {
      const id = node.dataset.reorderId;
      if (!id) return;
      const prev = first.get(id);
      if (!prev) return;
      const last = node.getBoundingClientRect();
      const dx = prev.left - last.left;
      const dy = prev.top - last.top;
      if (dx === 0 && dy === 0) return;

      node.style.transition = "none";
      node.style.transform = `translate(${dx}px, ${dy}px)`;
      // Force layout so the invert sticks before animating to identity.
      void node.offsetWidth;
      node.style.transition = `transform ${FLIP_MS}ms ${FLIP_EASING}`;
      node.style.transform = "";

      const clear = () => {
        node.style.transition = "";
        node.style.transform = "";
        node.removeEventListener("transitionend", clear);
      };
      node.addEventListener("transitionend", clear);
    });
  });
};

/** flushSync + FLIP so list rows glide instead of jumping. */
export const runSmoothReorder = (
  container: HTMLElement | null | undefined,
  apply: () => void
) => {
  animateReorderFlip(container, () => {
    flushSync(apply);
  });
};
